import { createElement } from "@wordpress/element";

/** @format */

/**
 * External dependencies
 */
import { __, sprintf } from '@wordpress/i18n';
import { Button } from '@wordpress/components';
import classnames from 'classnames';
import Gridicon from 'gridicons';
import { isNil, noop } from 'lodash';
import PropTypes from 'prop-types';
/**
 * Internal dependencies
 */

import Link from '../link';
/**
 * A component to show a value, label, and an optional change percentage. Can also act as a link to a specific report focus.
 *
 * @return { object } -
 */

var SummaryNumber = function SummaryNumber(_ref) {
  var delta = _ref.delta,
      href = _ref.href,
      isOpen = _ref.isOpen,
      label = _ref.label,
      onToggle = _ref.onToggle,
      prevLabel = _ref.prevLabel,
      prevValue = _ref.prevValue,
      reverseTrend = _ref.reverseTrend,
      selected = _ref.selected,
      value = _ref.value,
      onLinkClickCallback = _ref.onLinkClickCallback;
  var liClasses = classnames('woocommerce-summary__item-container', {
    'is-dropdown-button': onToggle,
    'is-dropdown-expanded': isOpen
  });
  var classes = classnames('woocommerce-summary__item', {
    'is-selected': selected,
    'is-good-trend': reverseTrend ? delta < 0 : delta > 0,
    'is-bad-trend': reverseTrend ? delta > 0 : delta < 0
  });
  var icon = delta > 0 ? 'arrow-up' : 'arrow-down';
  var screenReaderLabel = delta > 0 ? sprintf(__('Up %d%% from %s', 'woocommerce-admin'), delta, prevLabel) : sprintf(__('Down %d%% from %s', 'woocommerce-admin'), Math.abs(delta), prevLabel);

  if (!delta) {
    // delta is zero or falsey
    icon = 'arrow-right';
    screenReaderLabel = sprintf(__('No change from %s', 'woocommerce-admin'), prevLabel);
  }

  var Container;
  var containerProps = {
    className: classes,
    'aria-current': selected ? 'page' : null
  };

  if (onToggle || href) {
    var isButton = !!onToggle;
    Container = isButton ? Button : Link;

    if (isButton) {
      containerProps.onClick = onToggle;
      containerProps['aria-expanded'] = isOpen;
    } else {
      containerProps.href = href;
      containerProps.role = 'menuitem';
      containerProps.onClick = onLinkClickCallback;
    }
  } else {
    Container = 'div';
  }

  return createElement("li", {
    className: liClasses
  }, createElement(Container, containerProps, createElement("span", {
    className: "woocommerce-summary__item-label"
  }, label), createElement("span", {
    className: "woocommerce-summary__item-data"
  }, createElement("span", {
    className: "woocommerce-summary__item-value"
  }, !isNil(value) ? value : __('N/A', 'woocommerce-admin')), createElement("div", {
    className: "woocommerce-summary__item-delta",
    role: "presentation",
    "aria-label": screenReaderLabel
  }, createElement(Gridicon, {
    className: "woocommerce-summary__item-delta-icon",
    icon: icon,
    size: 18
  }), createElement("span", {
    className: "woocommerce-summary__item-delta-value"
  }, !isNil(delta) ? sprintf(__('%d%%', 'woocommerce-admin'), delta) : __('N/A', 'woocommerce-admin')))), createElement("span", {
    className: "woocommerce-summary__item-prev-label"
  }, prevLabel), ' '
  /* Add a real space so the line breaks here, and not in the label text. */
  , createElement("span", {
    className: "woocommerce-summary__item-prev-value"
  }, !isNil(prevValue) ? prevValue : __('N/A', 'woocommerce-admin')), onToggle ? createElement(Gridicon, {
    className: "woocommerce-summary__toggle",
    icon: "chevron-down",
    size: 24
  }) : null));
};

SummaryNumber.propTypes = {
  /**
   * A number to represent the percentage change since the last comparison period - positive numbers will show
   * a green up arrow, negative numbers will show a red down arrow, and zero will show a flat right arrow.
   * If omitted, no change value will display.
   */
  delta: PropTypes.number,

  /**
   * An internal link to the report focused on this number.
   */
  href: PropTypes.string,

  /**
   * Boolean describing whether the menu list is open. Only applies in mobile view,
   * and only applies to the toggle-able item (first in the list).
   */
  isOpen: PropTypes.bool,

  /**
   * A string description of this value, ex "Revenue", or "New Customers"
   */
  label: PropTypes.string.isRequired,

  /**
   * A function used to switch the given SummaryNumber to a button, and called on click.
   */
  onToggle: PropTypes.func,

  /**
   * A string description of the previous value's timeframe, ex "Previous Year:".
   */
  prevLabel: PropTypes.string,

  /**
   * A string or number value to display - a string is allowed so we can accept currency formatting.
   * If omitted, this section won't display.
   */
  prevValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /**
   * A boolean used to indicate that a negative delta is "good", and should be styled like a positive (and vice-versa).
   */
  reverseTrend: PropTypes.bool,

  /**
   * A boolean used to show a highlight style on this number.
   */
  selected: PropTypes.bool,

  /**
   * A string or number value to display - a string is allowed so we can accept currency formatting.
   */
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /**
   * A function to be called after a SummaryNumber, rendered as a link, is clicked.
   */
  onLinkClickCallback: PropTypes.func
};
SummaryNumber.defaultProps = {
  href: '',
  isOpen: false,
  prevLabel: __('Previous Period:', 'woocommerce-admin'),
  reverseTrend: false,
  selected: false,
  onLinkClickCallback: noop
};
export default SummaryNumber;
//# sourceMappingURL=number.js.map